/**
 * Inputs: trip + optional deck index. Outputs: swipeable bilingual show cards.
 * "Show this card" opens a chrome-free full screen for restaurant staff.
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { copyText } from "../lib/format.ts";
import { allergyLines } from "../lib/records.ts";
import { dietaryPhrasesForTrip, phraseKind, phraseTitle } from "@shared/phrases.ts";
import type { ThaiPhrase, TripDocument } from "@shared/schema.ts";
import { SwipeDeck } from "./SwipeDeck.tsx";

export function AllergyPhraseCard({
  phrase,
  title,
  bookingNote,
}: {
  phrase: ThaiPhrase;
  title: string;
  bookingNote?: string | null;
}) {
  const text = [title, phrase.english, phrase.thai, phrase.romanization].join("\n");
  const allergy = phraseKind(phrase) === "allergy";
  return (
    <section className={`show-card allergy-card ${allergy ? "allergy-priority" : ""}`}>
      <p className="kicker">{allergy ? "Allergy card" : "Dietary card"}</p>
      <h2 className="serif show-card-title">{title}</h2>
      {!phrase.translationReviewed ? <p className="banner">Not owner-reviewed yet.</p> : (
        <p className="muted">Reviewed{phrase.reviewedAt ? ` ${phrase.reviewedAt.slice(0, 10)}` : ""}.</p>
      )}
      <p className="phrase-thai" lang="th">{phrase.thai}</p>
      <p className="phrase-english">{phrase.english}</p>
      <p className="phrase-roman">{phrase.romanization}</p>
      {bookingNote ? <p className="allergy-disclaimer">{bookingNote}</p> : null}
      <div className="row-actions">
        <Link className="btn" to={`/show/${phrase.id}`} viewTransition>Show this card</Link>
        <button className="btn-secondary" type="button" onClick={() => void copyText(text)}>
          Copy Thai and English
        </button>
      </div>
    </section>
  );
}

export function AllergyBookingNotes({ trip }: { trip: TripDocument }) {
  const lines = allergyLines(trip);
  const text = ["Dietary notes as written on the booking", ...lines].join("\n");
  return (
    <section className="show-card allergy-card">
      <p className="kicker">As written on the booking</p>
      <h2 className="serif show-card-title">English notes</h2>
      {lines.length ? (
        <ul className="allergy-list">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">No allergy notes on the bookings yet.</p>
      )}
      <p className="allergy-disclaimer">These notes belong to a specific booking. They are not a food-safety guarantee for every restaurant.</p>
      <button className="btn-secondary" type="button" onClick={() => void copyText(text)}>
        Copy booking notes
      </button>
    </section>
  );
}

export function AllergyCard({
  trip,
  index = 0,
  onIndexChange,
  dock,
}: {
  trip: TripDocument;
  index?: number;
  onIndexChange?: (index: number) => void;
  dock?: ReactNode;
}) {
  const [localIndex, setLocalIndex] = useState(index);
  const dietary = dietaryPhrasesForTrip(trip.thaiPhrases);
  const slides = [
    ...dietary.map((phrase) => (
      <AllergyPhraseCard
        key={phrase.id}
        phrase={phrase}
        title={phraseTitle(phrase)}
        bookingNote={null}
      />
    )),
    <AllergyBookingNotes key="booking-notes" trip={trip} />,
  ];
  const current = onIndexChange ? index : localIndex;
  const setCurrent = onIndexChange ?? setLocalIndex;

  return (
    <SwipeDeck label="Show cards" index={current} onIndexChange={setCurrent} dock={dock}>
      {slides}
    </SwipeDeck>
  );
}
